import { useCustomer } from 'hooks/context/customer/customer';
import { useDelivery } from 'hooks/context/Delivery';
import { Address } from 'olo-service';
import { HOSTNAME } from 'olo-service/axiosInstance';
import { useMutation, useQuery } from 'react-query';
import { useColoAxiosInstance } from './useColoRequest';

export type DeliveryAddressPayload = {
  uuid?: string;
  label?: string;
  note?: string;
  phoneNumber?: string;
  preference?: string;
} & Address;

export const ADDRESS_KEYS = {
  address: ['address'] as const,
} as const;

export function useCreateAddress() {
  const axios = useColoAxiosInstance();
  const { sessionToken } = useCustomer();

  return useMutation(async (payload: DeliveryAddressPayload) => {
    const response = await axios.post<DeliveryAddressPayload>('oloservice/v1/customers/current/addresses', payload, {
      headers: {
        'X-Clover-Customer-Auth': sessionToken,
      },
      baseURL: HOSTNAME,
    });

    return response.data;
  });
}

export function useUpsertLatestAddress() {
  const axios = useColoAxiosInstance();
  const { sessionToken } = useCustomer();

  return useMutation(async (payload: DeliveryAddressPayload) => {
    const addressesResponse = await axios.get<DeliveryAddressPayload[]>('oloservice/v1/customers/current/addresses', {
      headers: {
        'X-Clover-Customer-Auth': sessionToken,
      },
      baseURL: HOSTNAME,
    });

    const existingAddress = selectLatestAddress(addressesResponse.data);

    if (existingAddress) {
      const updateResponse = await axios.put<DeliveryAddressPayload>(
        `oloservice/v1/customers/current/addresses/${existingAddress.uuid}`,
        payload,
        {
          headers: {
            'X-Clover-Customer-Auth': sessionToken,
          },
          baseURL: HOSTNAME,
        }
      );

      return updateResponse.data;
    } else {
      const createResponse = await axios.post<DeliveryAddressPayload>('oloservice/v1/customers/current/addresses', payload, {
        headers: {
          'X-Clover-Customer-Auth': sessionToken,
        },
        baseURL: HOSTNAME,
      });

      return createResponse.data;
    }
  });
}

/**
 * @param select - func that returns the selected address from an array of addresses
 * @param autoRun - if true, endpoint will be called on every render automatically if it is imported into a component
 */
export function useGetAddresses<T = DeliveryAddressPayload[]>(select?: (result: DeliveryAddressPayload[]) => T, autoRun?: boolean) {
  const axios = useColoAxiosInstance();
  const { deliveryAddress } = useDelivery();
  const { sessionToken, customer } = useCustomer();

  const isAutoRunEnabled = autoRun === undefined ? (!!sessionToken && !!customer?.globalCustomerUuid && !deliveryAddress) : autoRun;

  return useQuery(
    ADDRESS_KEYS.address,
    async () => {
      const response = await axios.get<DeliveryAddressPayload[]>('oloservice/v1/customers/current/addresses', {
        headers: {
          'X-Clover-Customer-Auth': sessionToken,
        },
        baseURL: HOSTNAME,
      });

      return response.data;
    },
    {
      enabled: isAutoRunEnabled,
      staleTime: Infinity,
      select,
    }
  );
}

export function useGetLatestAddress(autoRun?: boolean) {
  return useGetAddresses(selectLatestAddress, autoRun);
}

function selectLatestAddress(addresses: DeliveryAddressPayload[] | undefined) {
  if (addresses && addresses.length > 0) {
    //selects most recently added address (should never be more than 1, but just in case)
    return addresses[addresses.length-1];
  }

  return null;
}
